import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { CtaBanner } from "@/components/site/CtaBanner";
import { SERVICES } from "@/lib/site";

const ServiceBlock = ({ s, flip }) => (
  <section id={s.slug} className={`scroll-mt-28 py-20 md:py-28 ${flip ? "bg-[var(--grey)]" : "bg-white"}`} data-testid={`service-section-${s.slug}`}>
    <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-12 lg:grid-cols-12 lg:items-center">
      <Reveal className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
        <div className="relative overflow-hidden rounded-[2rem] aspect-[4/5] sm:aspect-[4/3] lg:aspect-[4/5]">
          <img src={s.image} alt={s.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] hover:scale-105" />
          <span className="absolute left-5 top-5 rounded-full bg-white/90 backdrop-blur px-4 py-1.5 font-display text-sm font-semibold text-[var(--navy)]">{s.number}</span>
        </div>
      </Reveal>
      <div className={`lg:col-span-6 ${flip ? "lg:order-1 lg:pr-8" : "lg:pl-8"}`}>
        <Reveal>
          <p className="eyebrow text-[var(--green)]">Service {s.number}</p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.06] text-[var(--navy)]">{s.longTitle || s.title}</h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-[var(--body)]">{s.description}</p>
        </Reveal>
        <ul className="mt-8 grid sm:grid-cols-2 gap-x-6">
          {s.items.map((it, i) => (
            <li key={it}>
              <Reveal delay={i * 0.04} y={14} className="flex items-start gap-3 border-b border-slate-200 py-3.5 text-[var(--ink)]">
                <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[var(--green-tint)]"><Check className="h-3 w-3 text-[var(--green)]" /></span>
                {it}
              </Reveal>
            </li>
          ))}
        </ul>
        <Reveal delay={0.2} className="mt-10">
          <Link to={`/quote?service=${s.slug}`} data-testid={`service-enquire-button-${s.slug}`}
            className="group inline-flex items-center gap-3 rounded-full bg-[var(--green)] px-7 py-4 font-semibold text-white transition-[background-color,transform] duration-300 hover:bg-[var(--green-hover)] hover:-translate-y-0.5">
            Enquire about {s.title.toLowerCase()} <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-45" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Services() {
  return (
    <>
      <Seo title="Home Improvements, Garden & Cleaning Oxfordshire | Easy Handyman"
        description="Interior painting, insulation and handyman jobs, garden maintenance and driveway pressure washing, deep cleaning and carpet cleaning across Oxfordshire." />
      <PageHero testId="services-hero" eyebrow="Our services"
        lines={["Property maintenance", "services in Oxfordshire."]}
        intro="Home improvements, garden maintenance and professional house cleaning – all from one friendly local team.">
        <Reveal delay={0.5} className="mt-10 flex flex-wrap gap-3">
          {SERVICES.map((s) => (
            <a key={s.slug} href={`#${s.slug}`} data-testid={`services-jump-${s.slug}`}
              className="rounded-full border border-white/25 px-5 py-2.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-white hover:text-[var(--navy)]">
              {s.title}
            </a>
          ))}
        </Reveal>
      </PageHero>
      {SERVICES.map((s, i) => <ServiceBlock key={s.slug} s={s} flip={i % 2 === 1} />)}
      <CtaBanner />
    </>
  );
}
